// MST
import { types, flow, cast, detach } from 'mobx-state-tree';

// Interfaces
import {
  IAssessmentPreparationDeleteDocumentAttrs,
  IAssessmentPreparationDocumentAnswerSheetAttrs,
  IAssessmentPreparationDocumentConsentAssessmentAttrs,
  IAssessmentPreparationDocumentDetailAssessmentAttrs,
  IAssessmentPreparationDocumentDetailAttrs,
  IAssessmentPreparationDocumentImageIconsAttrs,
  IAssessmentPreparationDocumentPICListAttrs,
  IAssessmentPreparationDocumentTemplateAttrs,
  IAssessmentPreparationDownloadDocumentAttrs,
  IAssessmentPreparationGetListAttrs,
  IAssessmentPreparationSubmitAttrs,
  IAssessmentPreparationUploadDocumentAttrs,
} from 'interfaces/back-office/assessment/preparation/preparation-view.interface';
import {
  IAssessmentPreparationApiListResponse,
  IAssessmentPreparationApiNullResponse,
  IAssessmentPreparationApiUploadDocumentResponse,
  IAssessmentPreparationApiDownloadDocumentResponse,
  IAssessmentPreparationDocumentDetailResponse,
  IAssessmentPreparationDocumentTemplateResponse,
  IAssessmentPreparationDocumentPICListResponse,
  IAssessmentPreparationDocumentDetailAssessmentResponse,
  IAssessmentPreparationDocumentConsentAssessmentResponse,
  IAssessmentPreparationDocumentImageIconsResponse,
  IAssessmentPreparationDocumentAnswerSheetResponse,
} from 'interfaces/back-office/assessment/preparation/preparation-api.interface';
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';

// Utils
import {
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_preconditionFormModel,
  withHelper_preconditionDocHeaderFooterModel,
  withHelper_preconditionDocTemplate,
  withHelper_preconditionPICList,
  withHelper_preconditionDocDetailAssessment,
  withHelper_preconditionConsentAssessment,
  withHelper_preconditionImageIcons,
  withHelper_preconditionAnswerSheets,
} from 'models/extensions/helper';
import { withHelper_fileDownload } from 'models/extensions/with-helper';

// Lodash
import cloneDeep from 'lodash/cloneDeep';

// Constant
import { LOADING } from 'constant/shared.constant';

// Types
const { optional, model, array } = types;

// Loading
const { BASE, ACTION, DOWNLOAD } = LOADING;

// Base Model
const AssessmentPreparationModel = model('AssessmentPreparation')
  .props({
    assessmentPreparation_loading: withHelper_commonLoadingModel,
    assessmentPreparation_list: optional(array(withHelper_preconditionFormModel), []),
    assessmentPreparation_DocHeader: optional(array(withHelper_preconditionDocHeaderFooterModel), []),
    assessmentPreparation_DocFooter: optional(array(withHelper_preconditionDocHeaderFooterModel), []),
    assessmentPreparation_DocTemplate: optional(withHelper_preconditionDocTemplate, {}),
    assessmentPreparation_PIC_list: optional(array(withHelper_preconditionPICList), []),
    assessmentPreparation_DocDetailAssessment: optional(withHelper_preconditionDocDetailAssessment, {}),
    assessmentPreparation_ConsentAssessment: optional(withHelper_preconditionConsentAssessment, {}),
    assessmentPreparation_ImageIcons: optional(withHelper_preconditionImageIcons, {}),
    assessmentPreparation_AnswerSheets: optional(withHelper_preconditionAnswerSheets, {}),
  })
  .actions(self => ({
    assessmentPreparation_MAP_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep({ ...self.assessmentPreparation_loading, [payload.type]: payload.value });

      detach(self.assessmentPreparation_loading);
      self.assessmentPreparation_loading = cast(map);
    },
    assessmentPreparation_MAP_LIST_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep(
        self.assessmentPreparation_list.map(result =>
          result.id === payload.id ? { ...result, loading: withHelper_commonMapLoading(result.loading, payload) } : result,
        ),
      );
      detach(self.assessmentPreparation_list);
      self.assessmentPreparation_list = cast(map);
    },
    assessmentPreparation_SET_LIST: (payload: IAssessmentPreparationApiListResponse['res']['data']): void => {
      self.assessmentPreparation_list = cast(payload);
      const map = cloneDeep(
        self.assessmentPreparation_list.map(el => {
          return { ...el, TTDAssesse: el.uploadFiles.find(el => el.status === 'TTD Assessee') };
        }),
      );
      self.assessmentPreparation_list = cast(map);
    },
    //new
    assessmentPreparationDocFile_SET_HEADER: (payload: IAssessmentPreparationDocumentDetailResponse['res']['data']): void => {
      self.assessmentPreparation_DocHeader = cast(payload?.headers);
    },
    assessmentPreparationDocFile_SET_FOOTER: (payload: IAssessmentPreparationDocumentDetailResponse['res']['data']): void => {
      self.assessmentPreparation_DocFooter = cast(payload?.footers);
    },
    assesmentPreparationDocFile_SET_TEMPLATE: (payload: IAssessmentPreparationDocumentTemplateResponse['res']['data']): void => {
      self.assessmentPreparation_DocTemplate = cast(payload);
    },
    assesmentPreparationDocFile_SET_PIC_LIST: (payload: IAssessmentPreparationDocumentPICListResponse['res']['data']): void => {
      self.assessmentPreparation_PIC_list = cast(payload);
    },
    assesmentPreparationDocFile_SET_DETAIL_ASSESSMENT: (
      payload: IAssessmentPreparationDocumentDetailAssessmentResponse['res']['data'],
    ): void => {
      self.assessmentPreparation_DocDetailAssessment = cast(payload);
    },
    assesmentPreparationDocFile_SET_CONSENT_ASSESSMENT: (
      payload: IAssessmentPreparationDocumentConsentAssessmentResponse['res']['data'],
    ): void => {
      self.assessmentPreparation_ConsentAssessment = cast(payload);
    },
    assesmentPreparationDocFile_SET_IMAGE_ICONS: (
      payload: IAssessmentPreparationDocumentImageIconsResponse['res']['data'],
    ): void => {
      self.assessmentPreparation_ImageIcons = cast(payload);
    },
    assesmentPreparationDocFile_SET_ANSWER_SHEETS: (
      payload: IAssessmentPreparationDocumentAnswerSheetResponse['res']['data'],
    ): void => {
      self.assessmentPreparation_AnswerSheets = cast(payload);
    },
    //
    assessmentPreparation_MAP_FILE: (
      payload: IAssessmentPreparationApiUploadDocumentResponse['res']['data'] & { id: number },
    ): void => {
      const map = cloneDeep(
        self.assessmentPreparation_list.map(result => (result.id === payload.id ? { ...result, ...payload } : result)),
      );

      detach(self.assessmentPreparation_list);
      self.assessmentPreparation_list = cast(map);
    },
    assessmentPreparation_CLEAR_LIST: (): void => {
      self.assessmentPreparation_list = cast([]);
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment preparation list
     *
     * @param {IAssessmentPreparationGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparation_fetchList: flow(function* (payload: IAssessmentPreparationGetListAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });

      const response: IAssessmentPreparationApiListResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assessmentPreparation_SET_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Upload assessment preparation document
     *
     * @param {IAssessmentPreparationUploadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparation_uploadDocument: flow(function* (payload: IAssessmentPreparationUploadDocumentAttrs) {
      const { preparationId: id } = payload.body;

      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: ACTION, value: true });
      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: true });

      const response: IAssessmentPreparationApiUploadDocumentResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_uploadDocument({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessmentPreparation_MAP_FILE({ id, ...response.res.data });

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: ACTION, value: false });
      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: false });
    }),

    /**
     * @description Delete assessment preparation document
     *
     * @param {IAssessmentPreparationApiDeleteDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparation_deleteDocument: flow(function* (payload: IAssessmentPreparationDeleteDocumentAttrs) {
      const { preparationId: id } = payload.body;
      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: ACTION, value: true });
      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: true });

      const response: IAssessmentPreparationApiNullResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_deleteDocument({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessmentPreparation_MAP_FILE({ id, userFileBlobId: null, userOriginalFileName: null });

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: ACTION, value: false });
      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: false });
    }),

    /**
     * @description Download assessment preparation document
     *
     * @param {IAssessmentPreparationDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparation_downloadDocument: flow(function* (payload: IAssessmentPreparationDownloadDocumentAttrs) {
      const { blobId: id } = payload.params;
      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: DOWNLOAD, value: true });

      const response: IAssessmentPreparationApiDownloadDocumentResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LIST_LOADING({ id, type: DOWNLOAD, value: false });
    }),

    /**
     * @description Submit assessment preparation
     *
     * @param {IAssessmentPreparationSubmitAttrs} payload
     *
     * @return {any} any
     */
    assessmentPreparation_submit: flow(function* (payload: IAssessmentPreparationSubmitAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: true });

      const response: IAssessmentPreparationApiNullResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_submit({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: ACTION, value: false });
    }),
    //new
    /**
     * @description Fetch assessment preparation document detail
     *
     * @param {IAssessmentPreparationDocumentDetailAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentDetail: flow(function* (payload: IAssessmentPreparationDocumentDetailAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentDetailResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          body: { ...payload.body },
        });

      if (response.ok) {
        self.assessmentPreparationDocFile_SET_HEADER(response.res.data);
        self.assessmentPreparationDocFile_SET_FOOTER(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document template
     *
     * @param {IAssessmentPreparationDocumentTemplateAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentTemplate: flow(function* (payload: IAssessmentPreparationDocumentTemplateAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentTemplateResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentTemplate({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_TEMPLATE(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document PIC list
     *
     * @param {IAssessmentPreparationDocumentPICListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentPICList: flow(function* (payload: IAssessmentPreparationDocumentPICListAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentPICListResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentPICList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_PIC_LIST(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document Detail Assessment
     *
     * @param {IAssessmentPreparationDocumentDetailAssessmentAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentDetailAssessment: flow(function* (payload: IAssessmentPreparationDocumentDetailAssessmentAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentDetailAssessmentResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentDetailAssessment({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_DETAIL_ASSESSMENT(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document Consent Assessment
     *
     * @param {IAssessmentPreparationDocumentConsentAssessmentAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentConsentAssesment: flow(function* (payload: IAssessmentPreparationDocumentConsentAssessmentAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentConsentAssessmentResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentConsentAssessment({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_CONSENT_ASSESSMENT(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document Image Icons
     *
     * @param {IAssessmentPreparationDocumentImageIconsAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentImageIcons: flow(function* (payload: IAssessmentPreparationDocumentImageIconsAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentImageIconsResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentImageIcons({
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_IMAGE_ICONS(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment preparation document Answer Sheets
     *
     * @param {IAssessmentPreparationDocumentAnswerSheetAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDocumentAnswerSheets: flow(function* (payload: IAssessmentPreparationDocumentAnswerSheetAttrs) {
      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: true });
      const response: IAssessmentPreparationDocumentAnswerSheetResponse =
        yield self.environment.assessmentPreparationApi.assessmentPreparation_DocumentAnswerSheet({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });
      if (response.ok) {
        self.assesmentPreparationDocFile_SET_ANSWER_SHEETS(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentPreparation_MAP_LOADING({ type: BASE, value: false });
    }),
  }));

export const createAssessmentPreparationStoreDefaultModel = () => optional(AssessmentPreparationModel, {});
